import { useEffect, useRef, useState } from 'react';
import { useForm } from 'react-hook-form';
import type { PlantingMergedResult } from '/@/components/field-list-v2/components/sidebar/hooks';
import { useMutateCreateOrUpdateCropMonitoring } from '/@/hooks/crop-monitoring/use-crop-monitoring';
import type { EVENTS as CROP_MONITORING_EVENTS } from '/@/store/tracking/events/crop-monitoring';
import { mpTrackEvent } from '/@/store/tracking/mixpanel-events';
import type { Field } from '/@/types/field';
import NewCropMonitoringFormModal from './components/NewCropMonitoringFormModal';
import ExitWithoutSavingModal from './ExitWithoutSavingModal';
import SuccessModal from './SuccessModal';
import type { CropMonitoringFormType } from './types';
import { FIELDVIEW_CROP_MAP } from './utils';

export type { CropMonitoringFormType } from './types';
export { MAX_ENVIRONMENT, MIN_ENVIRONMENT } from './types';

const CropMonitoringDialog = ({
    open,
    setClose,
    field,
    entry_source,
    plantingReport,
    forceNew = false,
}: {
    open: boolean;
    setClose: () => void;
    field?: Field;
    entry_source: Parameters<(typeof CROP_MONITORING_EVENTS)['Add Crop Flow create began']>[0]['entry_source'];
    plantingReport?: PlantingMergedResult;
    /** Always start a blank form, even if `field.activeCropMonitoring` is still set (e.g. a
     * campaign whose phenology already reached 'harvested' but has no explicit harvestDate yet). */
    forceNew?: boolean;
}) => {
    let cropMonitoring = field?.activeCropMonitoring;
    if (forceNew || cropMonitoring?.harvestDate) {
        cropMonitoring = undefined;
    }

    const onlyEdit = !!cropMonitoring?.id;

    const mappedCrop = (
        plantingReport ? (FIELDVIEW_CROP_MAP[plantingReport.cropName.toLowerCase()] ?? '') : ''
    ) as CropMonitoringFormType['crop'];

    const [showSuccess, setShowSuccess] = useState<boolean>(false);
    const [showExitWarning, setShowExitWarning] = useState<boolean>(false);

    const form = useForm<CropMonitoringFormType>({
        defaultValues: {
            plantingDate: cropMonitoring?.plantingDate ?? plantingReport?.plantingDate,
            product:
                cropMonitoring?.cornCropMonitoring?.product ??
                cropMonitoring?.productName ??
                plantingReport?.seedProductNames[0],
            technology: cropMonitoring?.cornCropMonitoring?.technology,
            environment: cropMonitoring?.environment,
            fieldId: cropMonitoring?.field || field?.id,
            id: cropMonitoring?.id,
            crop: cropMonitoring?.crop || mappedCrop || '',
            previousCrop: cropMonitoring?.previousCrop || '',
        },
    });

    useEffect(() => {
        if (!open || onlyEdit) return;
        mpTrackEvent('Add Crop Flow create began', { entry_source });
    }, [open]);

    // plantingReport arrives asynchronously (after the query resolves). Re-populate the
    // form once it lands, but only if the user hasn't touched anything yet.
    const plantingReportApplied = useRef(false);
    useEffect(() => {
        if (!plantingReport || onlyEdit || plantingReportApplied.current) return;
        plantingReportApplied.current = true;
        const crop = (FIELDVIEW_CROP_MAP[plantingReport.cropName.toLowerCase()] ??
            '') as CropMonitoringFormType['crop'];
        form.reset({
            plantingDate: plantingReport.plantingDate,
            product: plantingReport.seedProductNames[0],
            crop,
            previousCrop: '',
            environment: undefined,
            technology: undefined,
            fieldId: field?.id,
            id: undefined,
        });
    }, [plantingReport, onlyEdit, form.reset, field?.id]);

    useEffect(() => {
        if (!open) plantingReportApplied.current = false;
    }, [open]);

    const { plantingDate, environment, plantedArea, density, fieldId, technology, product, crop, previousCrop } =
        form.watch();

    const { trigger: createOrUpdateCropMonitoring, isMutating } = useMutateCreateOrUpdateCropMonitoring(
        {
            plantingDate,
            environment: environment || 0,
            plantedArea,
            density,
            field: field?.id || fieldId || 0,
            technology,
            product,
            crop: crop || undefined,
            previousCrop: previousCrop === 'none' ? undefined : previousCrop || undefined,
            id: cropMonitoring?.id,
            sourcePlantingReport: plantingReport?.report,
        },
        {
            onSuccess: () => {
                setShowSuccess(true);
            },
        }
    );

    const handleClose = () => {
        if (isMutating) return;
        const hasData = crop || plantingDate || environment || product || technology || previousCrop;
        if (hasData && !showSuccess) {
            setShowExitWarning(true);
        } else {
            setClose();
            form.reset();
        }
    };

    const handleDiscardAndExit = () => {
        setShowExitWarning(false);
        setClose();
        form.reset();
    };

    const handleSuccessClose = () => {
        setShowSuccess(false);
        setClose();
        form.reset();
    };

    if (!open) return null;

    return (
        <>
            <NewCropMonitoringFormModal
                open={open && !showSuccess && !showExitWarning}
                setClose={handleClose}
                form={form}
                field={field}
                onSubmit={() => createOrUpdateCropMonitoring()}
                submitting={isMutating}
                onlyEdit={onlyEdit}
                entry_source={entry_source}
                plantingReport={plantingReport}
            />
            <SuccessModal
                open={showSuccess}
                setClose={handleSuccessClose}
                crop={crop}
                fieldId={field?.id || fieldId || 0}
            />
            <ExitWithoutSavingModal
                open={showExitWarning}
                setClose={() => setShowExitWarning(false)}
                onDiscard={handleDiscardAndExit}
            />
        </>
    );
};

export default CropMonitoringDialog;
